import {
  faArrowLeft, faBuilding, faBuildingUser, faChalkboardUser, faEnvelope, faEye, faEyeSlash,
  faLock, faSchool, faTriangleExclamation, faUser, faUserGraduate, faUserPlus
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import Header from "../../Components/Header/Header";
import ModulesPicker from "../../Components/AccountForm/ModulesPicker";
import "../../Components/AccountForm/AccountForm.css";

const CreateAccount = ({ data, socket } : { data : any, socket?: any }) => {

    const [cookies] = useCookies();
    const en = cookies?.language === "en";
    const [error, setError] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const navigate = useNavigate();

    const [inputs, setInputs] = useState({
      username: "",
      firstName: "",
      lastName: "",
      email: "",
      school: "",
      type: "teacher",
      password: "",
      department: "",
      teacherUsername: "",
      modules: JSON.parse(data.userData?.modules || "0") || ["edit", "functional", "debug", "learn", "train", "test"]
    });

    const getTeacherId = async () => {
        try {
            const options = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                }
            };
            const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/get-teacher-id/${inputs.teacherUsername}`, options);
            const response = await request.json();
            if (response.success) return await response.teacherId;
            return null;
        } catch (e) {
        }

    };

    const signup = async (e : React.FormEvent) => {
      e.preventDefault();
      if (saving) return;

      setError("");
      if (!inputs.firstName.length || !inputs.lastName.length || !inputs.username.length || !inputs.email.length || !inputs.password.length)
        return setError(en ? "Fill in the required fields!" : "Completati campurile obligatorii!");
      const emailValidation = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!inputs.email.match(emailValidation))
        return setError(en ? "The email is not valid!" : "Email-ul nu este valid!");
      if (inputs.password.length < 8)
        return setError(en ? "The password must be at least 8 characters" : "Parola trebuie sa contina minim 8 caractere");

      setSaving(true);

      try {

        const teacherId = await getTeacherId();

        const options = {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            username: inputs.username,
            password: inputs.password,
            firstName: inputs.firstName,
            lastName: inputs.lastName,
            school: inputs.school,
            email: inputs.email,
            type: inputs.type,
            active: null,
            department: inputs.department,
            teacherId,
            modules: inputs.modules
          })
        };

        const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/signup`, options);
        const response = await request.json();
        if (response.success) {
          window.location.replace("/users");
        } else {
          setError(en ? response.message.en : response.message.ro);
          setSaving(false);
        }
      } catch (e) {
        setError(en ? "The server could not be reached." : "Serverul nu a putut fi contactat.");
        setSaving(false);
      }
    };

    const handleInputChange = (e : React.ChangeEvent<HTMLInputElement>) => {
      setInputs(currInputs => {
        return {
          ...currInputs,
          [e.target.name]: e.target.value
        };
      });
    };

    const toggleModule = (key: string) => {
      setInputs(currInputs => ({
        ...currInputs,
        modules: currInputs.modules.includes(key)
          ? currInputs.modules.filter((m: string) => m !== key)
          : [...currInputs.modules, key]
      }));
    };

    useEffect(() => {
        if (!data.userData.admin) navigate(-1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isTeacher = inputs.type === "teacher";

    const field = (
      name: "firstName" | "lastName" | "username" | "email" | "school" | "department" | "teacherUsername",
      label: string, placeholder: string, icon: any, type = "text",
    ) => (
      <label className="acc-field">
        <span>{label}</span>
        <div className="acc-input">
          <FontAwesomeIcon icon={icon} />
          <input
            type={type}
            name={name}
            value={(inputs as any)[name]}
            onChange={handleInputChange}
            placeholder={placeholder}
            autoComplete="off"
          />
        </div>
      </label>
    );

    return (
        <div className="acc-page">
            <Header data={data} socket={socket} />

            <div className="acc-card">
                <form className="acc-form" onSubmit={signup}>

                    <header className="acc-head">
                        <span className="acc-avatar"><FontAwesomeIcon icon={faUserPlus} /></span>
                        <div className="acc-head-text">
                            <h2 className="acc-title">{en ? "Create an account" : "Creează un cont"}</h2>
                            <p className="acc-sub">
                                {en
                                    ? "The account signs in with the username and password set here."
                                    : "Contul se autentifică cu numele de utilizator și parola stabilite aici."}
                            </p>
                        </div>
                    </header>

                    <section className="acc-section">
                        <p className="acc-kicker">
                            <span className="acc-kicker-dot" />
                            {en ? "Account type" : "Tipul contului"}
                        </p>

                        <div className="acc-roles">
                            <button
                                type="button"
                                className={`acc-role-btn${isTeacher ? " on" : ""}`}
                                aria-pressed={isTeacher}
                                onClick={() => setInputs(curr => ({ ...curr, type: "teacher" }))}
                            >
                                <span className="acc-module-icon"><FontAwesomeIcon icon={faChalkboardUser} /></span>
                                <span>
                                    <strong>{en ? "Teacher" : "Profesor"}</strong>
                                    <small>{en ? "Creates students and sessions" : "Creează elevi și sesiuni"}</small>
                                </span>
                            </button>

                            <button
                                type="button"
                                className={`acc-role-btn${!isTeacher ? " on" : ""}`}
                                aria-pressed={!isTeacher}
                                onClick={() => setInputs(curr => ({ ...curr, type: "student" }))}
                            >
                                <span className="acc-module-icon"><FontAwesomeIcon icon={faUserGraduate} /></span>
                                <span>
                                    <strong>{en ? "Student" : "Elev"}</strong>
                                    <small>{en ? "Works in the modules" : "Lucrează în module"}</small>
                                </span>
                            </button>
                        </div>
                    </section>

                    <section className="acc-section">
                        <p className="acc-kicker">
                            <span className="acc-kicker-dot" />
                            {en ? "Account details" : "Datele contului"}
                        </p>

                        <div className="acc-grid">
                            {field("firstName", en ? "First name *" : "Nume *", en ? "First name" : "Nume", faUser)}
                            {field("lastName", en ? "Last name *" : "Prenume *", en ? "Last name" : "Prenume", faUser)}
                            {field("username", en ? "Username *" : "Nume de utilizator *", en ? "Username" : "Nume de utilizator", faUser)}
                            {field("email", "Email *", "email@exemplu.ro", faEnvelope, "email")}

                            <label className="acc-field">
                                <span>{en ? "Password *" : "Parolă *"}</span>
                                <div className="acc-input">
                                    <FontAwesomeIcon icon={faLock} />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={inputs.password}
                                        onChange={handleInputChange}
                                        placeholder={en ? "At least 8 characters" : "Minim 8 caractere"}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="acc-eye"
                                        title={showPassword ? (en ? "Hide password" : "Ascunde parola") : (en ? "Show password" : "Arată parola")}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                        onClick={() => setShowPassword(curr => !curr)}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </button>
                                </div>
                            </label>

                            {field(
                                "school",
                                isTeacher ? (en ? "Company / school" : "Companie / școală") : (en ? "School" : "Școală"),
                                isTeacher ? (en ? "Company / school" : "Companie / școală") : (en ? "School" : "Școală"),
                                isTeacher ? faBuilding : faSchool,
                            )}

                            {isTeacher
                                ? field("department", en ? "Department" : "Departament", en ? "Department" : "Departament", faBuildingUser)
                                : (
                                    <div className="acc-field">
                                        {field("teacherUsername", en ? "Teacher username" : "Nume de utilizator profesor", en ? "Teacher username" : "Nume de utilizator profesor", faChalkboardUser)}
                                        <p className="acc-hint">
                                            {en
                                                ? "The student is attached to this teacher; left empty, the account has no teacher."
                                                : "Elevul este atribuit acestui profesor; lăsat gol, contul rămâne fără profesor."}
                                        </p>
                                    </div>
                                )}
                        </div>
                    </section>

                    <section className="acc-section">
                        <p className="acc-kicker">
                            <span className="acc-kicker-dot" />
                            {en ? "Module access" : "Acces la module"}
                        </p>

                        <ModulesPicker value={inputs.modules} onToggle={toggleModule} en={en} />
                    </section>

                    {error.length ? (
                        <p className="acc-error">
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            {error}
                        </p>
                    ) : null}

                    <div className="acc-actions">
                        <button type="submit" className="acc-btn" disabled={saving}>
                            {saving
                                ? (en ? "Creating…" : "Se creează…")
                                : (en ? "Create the account" : "Creează contul")}
                        </button>
                        <button type="button" className="acc-btn ghost" onClick={() => navigate("/users")}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                            {en ? "Back to users" : "Înapoi la utilizatori"}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default CreateAccount;
